// Security review: invalidate owner-scoped work synchronously and serialize cookie mutations.
export class SessionBoundary {
  epoch = 0;
  ready = false;
  ownerId = null;
  #tail = Promise.resolve();

  invalidate() { this.ready = false; this.ownerId = null; return ++this.epoch; }
  current(epoch) { return epoch === this.epoch; }
  activate(epoch, ownerId = null) { if (this.current(epoch)) { this.ready = true; this.ownerId = ownerId; } }
  assertCurrent(epoch) {
    if (!this.current(epoch)) {
      const error = new Error('Wallet or session changed. Retry with the current account.');
      error.code = 'STALE_SESSION';
      throw error;
    }
  }
  // Only cookie-changing HTTP calls belong in this queue, never wallet prompts.
  // A delayed verify response must finish before the invalidating logout runs.
  serialize(fn) {
    const result = this.#tail.then(fn);
    this.#tail = result.catch(() => {});
    return result;
  }
}

export const sessionBoundary = new SessionBoundary();

export async function readWalletIdentity(provider, { timeoutMs = 10_000 } = {}) {
  if (!provider?.request) return { address: null, chainId: null };
  let timer;
  let accounts, chainId;
  try {
    [accounts, chainId] = await Promise.race([
      Promise.all([provider.request({ method: 'eth_accounts' }), provider.request({ method: 'eth_chainId' })]),
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Wallet account lookup timed out')), timeoutMs); }),
    ]);
  } finally { clearTimeout(timer); }
  const address = accounts?.[0]?.toLowerCase() ?? null;
  if (address !== null && !/^0x[0-9a-f]{40}$/.test(address)) throw new Error('Wallet returned an invalid account');
  const id = Number(chainId);
  if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Wallet returned an invalid network');
  return { address, chainId: id };
}

export function sameIdentity(a, b) {
  return a.address === b.address && a.chainId === b.chainId;
}

export function sessionMatchesWallet(session, identity, expectedChainId) {
  return Boolean(session?.walletAddress && !session.isGuest &&
    session.walletAddress.toLowerCase() === identity.address &&
    identity.chainId === expectedChainId && Date.parse(session.expiresAt) > Date.now());
}
